import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/lib/supabase';

/**
 * usePendingPayments — invoices that have an in-flight payment WE initiated
 * (portal /pay or autopay) in the last 14 days: an orders.payments row
 * allocated to the invoice whose status is pending, processing, submitted OR
 * succeeded. Those invoices are LOCKED for payment until it clears (Sky,
 * 2026-10-22) — the pay endpoints refuse them too
 * (netlify/functions/_lib/payment-in-flight.ts, same statuses, same window).
 *
 * Returns a Map keyed by qbo_invoice_id; the value says whether any of the
 * clearing payments is a bank (ACH) payment, which takes 3–5 business days,
 * so the badge can say so. `.has(id)` works as it did when this was a Set.
 *
 * Why 'succeeded' is included: the QBO mirror still shows the invoice OPEN
 * until the payment posts and the sync runs. The consuming UI only shows the
 * badge while the invoice is NOT yet paid, so a paid invoice can't stick on
 * "Processing". The 14-day window keeps an old partial payment from locking an
 * invoice for ever. RLS scopes payment_invoice_allocations to the caller's own
 * payments.
 */
export interface PendingPayment {
  /** A bank (ACH) payment is among those clearing — 3–5 business days. */
  bank: boolean;
}

export function usePendingPayments() {
  return useQuery<Map<string, PendingPayment>>({
    queryKey: ['pending-payments'],
    queryFn: async () => {
      const since = new Date(Date.now() - 14 * 24 * 60 * 60 * 1000).toISOString();
      const { data, error } = await supabase
        .schema('orders')
        .from('payment_invoice_allocations')
        .select('qbo_invoice_id, payments!inner(status, created_at, type)')
        .in('payments.status', ['pending', 'processing', 'submitted', 'succeeded'])
        .gt('payments.created_at', since);
      if (error) throw error;
      const out = new Map<string, PendingPayment>();
      for (const r of (data ?? []) as Array<{ qbo_invoice_id: string; payments: { type?: string | null } | Array<{ type?: string | null }> }>) {
        const p = Array.isArray(r.payments) ? r.payments[0] : r.payments;
        const id = String(r.qbo_invoice_id);
        const bank = (p?.type ?? '') === 'ach';
        out.set(id, { bank: (out.get(id)?.bank ?? false) || bank });
      }
      return out;
    },
    staleTime: 30_000,
  });
}

/** The words for a locked invoice — one place so every surface agrees. */
export function processingNote(bank: boolean): string {
  return bank
    ? 'Your bank (ACH) payment is clearing. Bank payments take 3–5 business days to clear — this invoice is locked until then so it isn’t paid twice.'
    : 'Your payment is processing and will show as paid shortly — this invoice is locked until then so it isn’t paid twice.';
}
